import { createAjv } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { LoadingSpinner, Modal } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { ActivitySetupContext } from "../../../../../context/ActivitySetupContext";
import { CampaignSetupContext } from "../../../../../context/CampaignSetupContext";
import { KeywordGroupBrandSelectionContext } from "../../../../../context/KeywordGroupBrandSelectionContext";
import { useToast } from "../../../../../hooks/useToast";
import ButtonGroupDynamic from "../../../../common/ButtonGroupDynamic";
import Table from "../../../../common/table/Table";
import ToastNotification from "../../../../common/toastNotification/ToastNotification";
import { ActivitySetupFormWrapper } from "../../../activitySetupFormsWrapper/ActivitySetupFormsWrapper";
import "./brands-points-select.scss";

const BrandsPointsSelectReceiptScan = ({ data, path, uischema, handleChange }) => {
  const {
    loading,
    keywordGroupsBrandsData,
    toggleBrandItem,
    availableBrands,
    setSelectedBrands,
    handleRemovedBrandData,
  } = useContext(KeywordGroupBrandSelectionContext);

  useEffect(() => {
    if (data?.ids) {
      setSelectedBrands((prev) => {
        const newGloballySelected = [...prev, ...data.ids];
        const uniqueGloballySelected = Array.from(new Set(newGloballySelected));
        return uniqueGloballySelected;
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentStep, setCurrentStep] = useState(0);
  const [edit, setEdit] = useState(false);

  const [currentData, setCurrentData] = useState(data);

  const [showModal, setShowModal] = useState(!data);
  const { currentCampaignTypeConfig } = useContext(CampaignSetupContext);
  const { activity, removeRule, itemsToRemove } = useContext(ActivitySetupContext);
  const [processedItemsToRemove, setProcessedItemsToRemove] = useState(false);
  const { toast, showToast, closeToast } = useToast();
  const toggleModal = () => setShowModal(!showModal);
  const childUiSchemas = uischema.options?.detail?.elements || [];
  const primaryButtonWhileAdding = currentStep === 0 ? "Next" : "Add rule";
  const secondaryButtonWhileAdding = currentStep === 0 ? "Cancel" : "Previous";
  const primaryButtonWhileEditting = "Save";
  const secondaryButtonWhileEditting = "Cancel";
  const ajv = createAjv();

  const {
    core: { schema: coreSchema, uischema: coreUiSchema },
  } = useJsonForms();

  const headerData = [
    {
      header: "Brand",
      columnKey: "brand",
    },
    {
      header: "ID",
      columnKey: "id",
    },
  ];

  const rowData = keywordGroupsBrandsData
    ?.filter((keywordGroup) => currentData?.ids?.includes(keywordGroup.id))
    .map((keywordGroup) => ({
      brand: keywordGroup.name,
      id: keywordGroup.id || "-",
    }));

  const onNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const onPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const onCancel = () => {
    if (!currentData) {
      removeRule(coreUiSchema.ruleSlug, coreUiSchema.occurrence);
      toggleModal();
      setEdit(false);
      return;
    }
    handleChange(path, currentData);
    toggleModal();
    setEdit(false);
  };

  const onOpenEdit = (step) => {
    setEdit(true);
    setCurrentStep(step);
    toggleModal();
  };

  // Avoiding infinite loop
  useEffect(() => {
    if (itemsToRemove?.length > 0 && !processedItemsToRemove) {
      handleRemovedBrandData(itemsToRemove);
      setProcessedItemsToRemove(true);
    } else if (itemsToRemove?.length === 0 && processedItemsToRemove) {
      setProcessedItemsToRemove(false);
    }
  }, [itemsToRemove, handleRemovedBrandData, processedItemsToRemove]);

  const onRemoveSet = () => {
    removeRule(coreUiSchema.ruleSlug, coreUiSchema.occurrence);
  };

  const onSave = () => {
    let currentSchema;
    let currentData;

    if (edit) {
      // Map steps to actual property names
      // Step 0 = brands (ids), Step 1 = points/threshold
      const propertyKey = currentStep === 0 ? "ids" : uischema.pointsKey || "points";

      currentSchema = coreSchema.properties[propertyKey];
      currentData = data[propertyKey];
    } else {
      currentSchema = coreSchema;
      currentData = data;
    }

    const isValid = ajv.validate(currentSchema, currentData);
    const editSuccessfulText = currentStep === 0 ? "Brands" : "Points per $ spent";
    if (!isValid) {
      showToast("error", "Please fill all the required fields", "");
      return;
    }

    if (Array.isArray(data?.ids)) {
      toggleBrandItem(data.ids);
    }

    setCurrentData(data);
    toggleModal();
    setEdit(false);
    showToast(
      "success",
      `${edit ? editSuccessfulText : "Brands and Points per $ spent"} successfully added to activity`,
      ""
    );
  };

  const primaryActionWhileAdding = currentStep === 0 ? onNextStep : onSave;
  const primaryActionWhileEditting = onSave;
  const secondaryActionWhileAdding = currentStep === 0 ? onCancel : onPreviousStep;
  const secondaryActionWhileEditting = onCancel;

  const isOnlyOccurrence =
    activity?.activityRules?.filter(
      (rule) => rule.ruleDefinition.slug === coreUiSchema.ruleSlug
    ).length === 1;

  const rulesConfiguration =
    activity.activityDefinition.rulesConfiguration ||
    currentCampaignTypeConfig.requirement.possibleActivities.find(
      (a) => a.id === activity.activityDefinition.id
    ).rulesConfiguration;

  const isRequiredConfiguration =
    rulesConfiguration?.rules.find((rule) => rule.rule?.slug === uischema.ruleSlug)
      ?.minRequired > 0;

  const isRequired =
    isRequiredConfiguration ||
    activity.activityDefinition.requiredRules?.includes(coreUiSchema.ruleSlug);

  return (
    <div className="brands-points-sets">
      {currentData && (
        <div
          className="brands-points-details"
          key={`${coreUiSchema.ruleSlug}-${coreUiSchema.occurrence}`}
        >
          <div className="brands-points-header">
            <span className="font-body-bold">
              Participating Brands ({coreUiSchema.occurrence})
            </span>
            <ButtonGroupDynamic
              size="small"
              items={[
                {
                  icon: "delete",
                  disabled: isOnlyOccurrence && isRequired,
                  onClick: onRemoveSet,
                },
              ]}
            />
          </div>
          <div className="brands-details-container">
            {loading ? (
              <div className="loading-container">
                <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
              </div>
            ) : (
              <>
                <Table
                  headerData={headerData}
                  rowData={rowData}
                  pagination={true}
                  rowsPerPage={10}
                />
                <ButtonGroupDynamic
                  size="small"
                  items={[
                    {
                      icon: "edit",
                      onClick: () => onOpenEdit(0),
                    },
                  ]}
                />
              </>
            )}
          </div>
          <div className="brands-details-container align-items-center">
            <div className="brand-points-labels">
              <span className="font-xsmall-bold">
                Set {coreUiSchema.occurrence}:{" "}
                {uischema.pointsLabel || "Points per $ spent"}
              </span>
              <span className="font-xsmall-regular">
                {currentData?.[uischema.pointsKey || "points"]}
              </span>
            </div>
            <ButtonGroupDynamic
              size="small"
              items={[
                {
                  icon: "edit",
                  onClick: () => onOpenEdit(1),
                },
              ]}
            />
          </div>
        </div>
      )}
      <Modal
        className="brands-rule-setup-modal"
        showModal={showModal}
        onCloseModal={onCancel}
        title={`${edit ? "Edit" : "Add"} rule - ${uischema.title}`}
        showTertiaryButton={false}
        showLink={false}
        secondaryButtonProps={{
          size: "medium",
          text: edit ? secondaryButtonWhileEditting : secondaryButtonWhileAdding,
          variant: "tertiary",
          onClick: edit ? secondaryActionWhileEditting : secondaryActionWhileAdding,
        }}
        primaryButtonProps={{
          size: "medium",
          text: edit ? primaryButtonWhileEditting : primaryButtonWhileAdding,
          variant: "primary",
          disabled: !edit && availableBrands?.length === 0,
          onClick: edit ? primaryActionWhileEditting : primaryActionWhileAdding,
        }}
      >
        <div className="w-100 col p-0 overflow-y-auto">
          <ActivitySetupFormWrapper
            jsonschema={coreSchema}
            uischema={childUiSchemas[currentStep]}
            data={data}
            onChange={(data) => handleChange(path, data)}
          />
        </div>
        <ToastNotification {...toast} handleClose={closeToast} />
      </Modal>
      {!showModal && <ToastNotification {...toast} handleClose={closeToast} />}
    </div>
  );
};

export const BrandsPointsSelectReceiptScanControl = withJsonFormsControlProps(
  BrandsPointsSelectReceiptScan
);
